import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import type { Game, OpponentUser, User } from "../lib/types";

interface Props {
  user: User;
  opponentsCache: Map<number, string>;
  refreshOpponents: () => Promise<OpponentUser[]>;
}

export function Home({ user, opponentsCache, refreshOpponents }: Props) {
  const [games, setGames] = useState<Game[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [g] = await Promise.all([api.listGames(), refreshOpponents()]);
        if (!cancelled) setGames(g.games);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      }
    }
    load();
    const t = setInterval(load, 30000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [refreshOpponents]);

  return (
    <div className="page page-pad-bottom">
      <div className="flex-row" style={{ justifyContent: "space-between", marginBottom: "1rem" }}>
        <h1 style={{ margin: 0 }}>Hi, {user.displayName}</h1>
        <Link to="/new" className="btn">+ New game</Link>
      </div>

      {error && <div className="card" style={{ color: "var(--warn)" }}>{error}</div>}
      {games === null && <p className="muted">Loading your games…</p>}

      {games && games.length === 0 && (
        <div className="card empty-state">
          <p className="big">No games yet.</p>
          <Link to="/new" className="btn">Start your first game</Link>
        </div>
      )}

      {games && games.map((g) => (
        <GameCard key={g.id} game={g} user={user} opponents={opponentsCache} />
      ))}
    </div>
  );
}

function GameCard({ game, user, opponents }: { game: Game; user: User; opponents: Map<number, string> }) {
  const oppId = game.whiteUserId === user.id ? game.blackUserId : game.whiteUserId;
  const oppName = opponents.get(oppId) ?? `Player ${oppId}`;
  const yourColor: "w" | "b" = game.whiteUserId === user.id ? "w" : "b";
  const yourTurn = game.status === "active" && game.turn === yourColor;
  const finished = game.status === "finished";

  let status;
  if (finished) {
    if (game.winnerUserId === null) status = <span className="finished">Draw</span>;
    else if (game.winnerUserId === user.id) status = <span className="finished" style={{ color: "var(--good)" }}>You won</span>;
    else status = <span className="finished" style={{ color: "var(--warn)" }}>You lost</span>;
  } else if (yourTurn) {
    status = <span className="your-turn">Your turn</span>;
  } else {
    status = <span className="waiting">Waiting…</span>;
  }

  return (
    <Link to={`/game/${game.id}`} className="card card-game">
      <div>
        <div className="vs">vs {oppName}</div>
        <div className="meta">
          {game.mode === "live" ? "Live" : "Async"} · You play {yourColor === "w" ? "White" : "Black"}
        </div>
      </div>
      <div>{status}</div>
    </Link>
  );
}
